import { useState, useEffect, useCallback } from 'react';
import { Project } from '../types';
import { apiService } from '../services/api';

export const useProjects = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProjects = useCallback(async (params?: { 
    status?: string; 
    limit?: number; 
    offset?: number; 
  }) => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getProjects(params);
      setProjects(response.projects);
    } catch (err) {
      console.error('Failed to fetch projects:', err);
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const createProject = async (projectData: {
    name: string;
    description: string;
    projectType: string;
    techStack?: string[];
    architecture?: string;
    existingAssets?: string[];
    totalBudget?: number;
    targetEndDate?: string;
  }): Promise<Project | null> => {
    try {
      const response = await apiService.createProject(projectData);
      setProjects(prev => [response.project, ...prev]);
      return response.project;
    } catch (err) {
      console.error('Failed to create project:', err);
      setError('Failed to create project');
      return null;
    }
  };

  const updateProject = async (id: string, updates: Partial<Project>): Promise<boolean> => {
    try {
      const response = await apiService.updateProject(id, updates);
      setProjects(prev => prev.map(p => p.id === id ? response.project : p));
      return true;
    } catch (err) {
      console.error('Failed to update project:', err);
      setError('Failed to update project');
      return false;
    }
  };

  const deleteProject = async (id: string): Promise<boolean> => {
    try {
      await apiService.deleteProject(id);
      setProjects(prev => prev.filter(p => p.id !== id));
      return true;
    } catch (err) {
      console.error('Failed to delete project:', err);
      setError('Failed to delete project');
      return false;
    }
  };

  const refreshProjects = () => {
    fetchProjects();
  };

  return {
    projects,
    loading,
    error,
    createProject,
    updateProject,
    deleteProject,
    refreshProjects,
    refetch: fetchProjects
  };
};